import { Link, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Youtube } from "lucide-react";
import logo from "@/assets/scrubbles_clean_logo.png"

const Footer = () => {
  const location = useLocation();

  // Hide footer on admin page
  if (location.pathname === "/admin") return null;

  return (
    <footer className="bg-slate-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-1">
              <Link to="/" data-testid="logo-link-footer">
                <img className="-mt-6 -ml-4 w-auto h-32 brightness-0 invert flex items-center justify-center" src={logo} />
              </Link>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mt-4">
              Family owned and operated since 2010. Award-winning laundromat serving Elmira and surrounding communities.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/Scrubbleslaundry/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-sky-600 transition-colors"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="#"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-sky-600 transition-colors"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              {/*}
              <a
                href="#"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-sky-600 transition-colors"
                data-testid="social-youtube"
              >
                <Youtube className="w-5 h-5" />
              </a>
              */}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Quick Links</h3>
            <ul className="space-y-3">
              {[
                { path: "/services", label: "Our Services" },
                { path: "/booking", label: "Book a Pickup" },
                { path: "/about", label: "About Us" },
                { path: "/faq", label: "FAQ" },
                { path: "/contact", label: "Contact Us" },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-sky-400 transition-colors text-sm"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Services</h3>
            <ul className="space-y-3">
              {[
                { path: "#", label: "Self-Serve Laundry" },
                { path: "wdf", label: "Wash & Fold" },
                { path: "drc", label: "Dry Cleaning" },
                { path: "/freshgear", label: "Fresh Gear" },
                { path: "/water", label: "Water Refills" },
                { path: "/booking", label: "Pickup & Delivery" },
              ].map((service) => (
                <li key={service.path}>
                  {service.path !== "wdf" && service.path !== "drc" ? (
                    <Link 
                      to={service.path} 
                      className="text-slate-400 hover:text-sky-400 transition-colors text-sm" 
                      data-testid={`footer-link-${service.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                    {service.label}
                  </Link>
                  ) : (
                    <span className="text-slate-400 text-sm">{service.label}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-sky-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-400 text-sm">
                  7-112 Oriole Parkway<br />
                  Elmira, ON N3B 1C5
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-sky-500 flex-shrink-0" />
                <a href="tel:519-210-9274" className="text-slate-400 hover:text-sky-400 text-sm transition-colors">
                  519-210-9274
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-sky-500 flex-shrink-0" />
                <a href="mailto:support@scrubbleslaundryandmore.org" className="text-slate-400 hover:text-sky-400 text-sm transition-colors break-all">
                  support@scrubbleslaundry.ca
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-sky-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-400 text-sm">
                  Open: 6am - 11pm Daily<br />
                  <br/>
                  Attended Service Hours: <br />
                  Wednesday & Friday: 10am - 6pm<br/>
                  Saturday & Sunday: 10am - 5pm<br/>
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © 2010 - {new Date().getFullYear()} Scrubbles Laundry & More. All rights reserved.
            </p>
            <p className="text-slate-500 text-sm">
              Record Readers' Choice Award Winner 2014-2024              
            </p>
            <div className="flex items-center gap-6">
              <Link
                to="/terms"
                className="text-slate-400 hover:text-sky-400 transition-colors text-sm"
                data-testid="footer-link-terms"
              >
                Terms of Service
              </Link>
              <Link
                to="/privacy"
                className="text-slate-400 hover:text-sky-400 transition-colors text-sm"
                data-testid="footer-link-privacy"
              >
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
